import React, { createContext, useContext, useState, useCallback } from 'react';
import AlertModal from './AlertModal';

interface AlertContextType {
    showAlert: (message: string) => Promise<void>;
    showConfirm: (message: string) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [type, setType] = useState<'alert' | 'confirm'>('alert');
    const [resolvePromise, setResolvePromise] = useState<((value: any) => void) | null>(null);

    const showAlert = useCallback((msg: string) => {
        setMessage(msg);
        setType('alert');
        setIsOpen(true);
        return new Promise<void>((resolve) => {
            setResolvePromise(() => resolve);
        });
    }, []);

    const showConfirm = useCallback((msg: string) => {
        setMessage(msg);
        setType('confirm');
        setIsOpen(true);
        return new Promise<boolean>((resolve) => {
            setResolvePromise(() => resolve);
        });
    }, []);

    const handleClose = useCallback((result: boolean = false) => {
        setIsOpen(false);
        if (resolvePromise) {
            if (type === 'confirm') {
                resolvePromise(result);
            } else {
                resolvePromise(undefined);
            }
            setResolvePromise(null);
        }
    }, [resolvePromise, type]);

    return (
        <AlertContext.Provider value={{ showAlert, showConfirm }}>
            {children}
            <AlertModal
                isOpen={isOpen}
                message={message}
                type={type}
                onClose={() => handleClose(false)}
                onConfirm={() => handleClose(true)}
            />
        </AlertContext.Provider>
    );
}

export function useAlert() {
    const context = useContext(AlertContext);
    if (context === undefined) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
}
